import axios, { AxiosInstance, InternalAxiosRequestConfig } from 'axios'
import { CONFIG } from '../../config'

// Create axios instance
export const api: AxiosInstance = axios.create({
  baseURL: CONFIG.service.baseUrl,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Request interceptor to add auth token
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = localStorage.getItem('admin_access_token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    
    // Add provider API key header for additional security
    const apiKey = CONFIG.provider.apiKey
    if (apiKey) {
      config.headers['X-Provider-Api-Key'] = apiKey
    }
    
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Response interceptor to handle token refresh
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config
    
    // If 401 and not already retried, try to refresh token
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true
      
      try {
        const refreshToken = localStorage.getItem('admin_refresh_token')
        if (refreshToken) {
          const response = await api.post('/auth/refresh', { refreshToken })
          const newToken = response.data.accessToken
          
          if (newToken) {
            localStorage.setItem('admin_access_token', newToken)
            originalRequest.headers.Authorization = `Bearer ${newToken}`
            return api(originalRequest)
          }
        }
      } catch (refreshError) {
        // Refresh failed, clear auth and redirect to login
        localStorage.removeItem('admin_access_token')
        localStorage.removeItem('admin_refresh_token')
        window.location.href = '/login'
        return Promise.reject(refreshError)
      }
    }
    
    // Handle other error status codes
    if (error.response?.status === 403) {
      console.error('Forbidden: Insufficient permissions')
    } else if (error.response?.status === 500) {
      console.error('Server error:', error.response.data)
    }
    
    return Promise.reject(error)
  }
)

// Admin management API endpoints
export const adminApi = {
  // Admin user management
  listAdmins: (params?: { 
    page?: number; 
    limit?: number; 
    search?: string; 
    role?: string; 
    status?: 'active' | 'suspended' | 'all';
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
  }) => {
    return api.get('/admin/adminUsers', { params })
  },
  
  createAdmin: (data: {
    name: string;
    email: string;
    role: string;
    password: string;
    sendWelcomeEmail?: boolean;
  }) => {
    return api.post('/admin/adminUsers', data)
  },
  
  getAdminDetails: (adminId: string) => {
    return api.get(`/admin/adminUsers/${adminId}`)
  },
  
  updateAdmin: (adminId: string, data: {
    name?: string;
    email?: string;
    role?: string;
    isSuspended?: boolean;
  }) => {
    return api.put(`/admin/adminUsers/${adminId}`, data)
  },
  
  deleteAdmin: (adminId: string) => {
    return api.delete(`/admin/adminUsers/${adminId}`)
  },
  
  resetAdminPassword: (adminId: string, data: {
    sendEmail?: boolean;
    temporaryPassword?: string;
    forceChangeOnLogin?: boolean;
  }) => {
    return api.post(`/admin/adminUsers/${adminId}/reset-password`, data)
  },
  
  getAdminAuditLogs: (adminId: string, params?: {
    page?: number;
    limit?: number;
    action?: string;
    dateFrom?: string;
    dateTo?: string;
  }) => {
    return api.get(`/admin/adminUsers/${adminId}/audit-logs`, { params })
  },
  
  // Bulk operations
  suspendAdmin: (adminId: string) => {
    return api.patch(`/admin/adminUsers/${adminId}/suspend`)
  },
  
  unsuspendAdmin: (adminId: string) => {
    return api.patch(`/admin/adminUsers/${adminId}/unsuspend`)
  },
}

// Provider-specific API endpoints
export const providerApi = {
  // Authentication
  login: (apiKey: string, secretKey: string) => {
    return api.post('/admin/auth/login', { apiKey, secretKey })
  },
  
  logout: () => {
    return api.post('/admin/auth/logout')
  },
  
  refreshToken: (refreshToken: string) => {
    return api.post('/admin/auth/refresh', { refreshToken })
  },
  
  // System monitoring
  getSystemMetrics: () => {
    return api.get('/admin/monitoring/metrics')
  },
  
  getSystemAlerts: (params?: { limit?: number; status?: string }) => {
    return api.get('/admin/monitoring/alerts', { params })
  },
  
  // Client management
  getClients: (params?: { page?: number; limit?: number; search?: string; status?: string }) => {
    return api.get('/admin/clients', { params })
  },
  
  getClientDetails: (clientId: string) => {
    return api.get(`/admin/clients/${clientId}`)
  },
  
  updateClientStatus: (clientId: string, status: string) => {
    return api.patch(`/admin/clients/${clientId}/status`, { status })
  },
  
  getClientActivity: (clientId: string, params?: { from?: string; to?: string }) => {
    return api.get(`/admin/clients/${clientId}/activity`, { params })
  },
  
  // Operations
  runBulkOperation: (operation: string, targets: string[], params: any) => {
    return api.post('/admin/operations/bulk', { operation, targets, params })
  },
  
  // Logs and analytics
  getSystemLogs: (params?: { level?: string; from?: string; to?: string; limit?: number }) => {
    return api.get('/admin/logs', { params })
  },
  
  getAnalytics: (params?: { metric?: string; period?: string; groupBy?: string }) => {
    return api.get('/admin/analytics', { params })
  },
}

// User management API endpoints
export const userApi = {
  // List users with pagination and filters
  listUsers: (params?: { 
    page?: number
    limit?: number
    search?: string
    status?: 'active' | 'inactive' | 'suspended' | 'all'
    emailVerified?: boolean | 'all'
    sortBy?: string
    sortOrder?: 'asc' | 'desc'
    dateFrom?: string
    dateTo?: string
  }) => {
    return api.get('/admin/users', { params })
  },

  // Get user by ID
  getUserById: (userId: string) => {
    return api.get(`/admin/users/${userId}`)
  },

  // Search users
  searchUsers: (query: string) => {
    return api.get('/admin/users/search', { params: { q: query } })
  },

  // Suspend user
  suspendUser: (userId: string, data: { reason?: string }) => {
    return api.post(`/admin/users/${userId}/suspend`, data)
  },

  // Unsuspend user
  unsuspendUser: (userId: string) => {
    return api.post(`/admin/users/${userId}/unsuspend`)
  },

  // Reset user password
  resetUserPassword: (userId: string, data: { sendEmail: boolean }) => {
    return api.post(`/admin/users/${userId}/reset-password`, data)
  },

  // Get user activity logs
  getUserActivityLogs: (userId: string) => {
    return api.get(`/admin/users/${userId}/activity-logs`)
  },

  // Get user statistics
  getUserStats: () => {
    return api.get('/admin/users/stats')
  },
  
  // Get user devices
  getUserDevices: (userId: string) => {
    return api.get(`/admin/users/${userId}/devices`)
  },

  // Get user organizations
  getUserOrganizations: (userId: string) => {
    return api.get(`/admin/users/${userId}/organizations`)
  },

  // Get user organization ownerships
  getUserOrganizationOwnerships: (userId: string) => {
    return api.get(`/admin/users/${userId}/organization-ownerships`)
  },

  // Export users
  exportUsers: (format: 'csv' | 'excel', filters?: any) => {
    return api.post('/admin/users/export', { format, filters }, {
      responseType: 'blob'
    })
  },
}

export default api